import React from 'react';
import { ChakraProvider, defaultSystem } from '@chakra-ui/react';
import { Toaster } from './components/toaster';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { FleetPage } from './pages/FleetPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { FleetListingAdminPage } from './pages/FleetListingAdminPage';
import { BoletosAdminPage } from './pages/BoletosAdminPage';
import { SelectDealerPage } from './pages/SelectDealerPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { authService } from './services/authService';
import { usePermissions } from './hooks/usePermissions';

// Personalização do sistema com as cores do logo AutoAvaliar
const customSystem = {
  ...defaultSystem,
  colors: {
    brand: {
      blue: '#3333CC', // Azul principal
      black: '#1A1A1A'   // Preto do logo
    }
  }
};

// Componente para redirecionamento inteligente baseado no role
const SmartRedirect: React.FC = () => {
  const { isPartner, isAdmin, isUser, isMaster } = usePermissions();

  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (isMaster()) {
    return <Navigate to="/master/select-dealer" replace />;
  } else if (isPartner()) {
    return <Navigate to="/fleet" replace />;
  } else if (isAdmin()) {
    return <Navigate to="/admin/fleet-listing" replace />;
  } else if (isUser()) {
    return <Navigate to="/home" replace />;
  } else {
    // Fallback para login se não conseguir determinar o role
    return <Navigate to="/login" replace />;
  }
};

// Configuração das rotas
const router = createBrowserRouter([
  {
    path: '/',
    element: <SmartRedirect />
  },
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/change-password',
    element: <ChangePasswordPage />
  },
  {
    // Rota protegida para HomePage - apenas User pode acessar
    element: <ProtectedRoute allowedRoles={['ROLE_USER']} />,
    children: [
      {
        path: '/home',
        element: <HomePage />
      }
    ]
  },
  {
    // Rotas protegidas para Partner - cadastro de frota e dashboard
    element: <ProtectedRoute allowedRoles={['ROLE_DEALER']} />,
    children: [
      {
        path: '/fleet',
        element: <FleetPage />
      },
      {
        path: '/dashboard',
        element: <Dashboard />
      }
    ]
  },
  {
    // Rota protegida exclusiva para Admin - Fleet Listing
    element: <ProtectedRoute allowedRoles={['ROLE_ADMIN']} />,
    children: [
      {
        path: '/admin/fleet-listing',
        element: <FleetListingAdminPage />
      },
      {
        path: '/admin/boletos',
        element: <BoletosAdminPage />
      }
    ]
  },
  {
    // Rota protegida exclusiva para Master - escolhe a concessionária para emitir em nome dela
    element: <ProtectedRoute allowedRoles={['ROLE_MASTER']} />,
    children: [
      {
        path: '/master/select-dealer',
        element: <SelectDealerPage />
      }
    ]
  }
]);

function App() {
  return (
    <ChakraProvider value={customSystem}>
      <RouterProvider router={router} />
      <Toaster />
    </ChakraProvider>
  );
}

export default App;